import { create } from 'zustand';
import type { PartyMember, EditContext, EncounterResult, RegionFormData, AnyItem } from '../types/types';
import { logger } from '../services/logger.client';

interface UiState {
  // Edit Modal
  isEditModalOpen: boolean;
  editModalContext: EditContext<AnyItem> | null;

  // Character Sheet Modal
  isCharacterSheetModalOpen: boolean;
  characterSheetPlayer: PartyMember | null;

  // Other Modals from ModalProviders
  isDiceRollerOpen: boolean;
  isSoundscapeOpen: boolean;
  isWatchOrderOpen: boolean;
  isSessionOpen: boolean;
  isScratchpadOpen: boolean;
  isCampaignLogOpen: boolean;
  isEncounterOpen: boolean;
  encounterResult: EncounterResult | null;
  isAddImageOpen: boolean;
  isAddEventOpen: boolean;
  isImageSelectOpen: boolean;
  imageSelectCb: (id: number) => void;
  isAddTerrainOpen: boolean;
  isCreateRegionOpen: boolean;
  createRegionCb: (data: RegionFormData) => void;
  isGridSettingsOpen: boolean;
  isAddRelationshipOpen: boolean;
  isAddManualCombatantOpen: boolean;
}

interface UiActions {
  // Edit Modal
  showEditModal: (context: EditContext<AnyItem>) => void;
  hideEditModal: () => void;

  // Character Sheet Modal
  showCharacterSheetModal: (player: PartyMember) => void;
  hideCharacterSheetModal: () => void;

  // Other Modals
  showDiceRollerModal: () => void;
  hideDiceRollerModal: () => void;
  showSoundscapeModal: () => void;
  hideSoundscapeModal: () => void;
  showWatchOrderModal: () => void;
  hideWatchOrderModal: () => void;
  showSessionModal: () => void;
  hideSessionModal: () => void;
  showScratchpadModal: () => void;
  hideScratchpadModal: () => void;
  showCampaignLogModal: () => void;
  hideCampaignLogModal: () => void;
  showEncounterModal: (result: EncounterResult | null) => void;
  hideEncounterModal: () => void;
  showAddImageModal: () => void;
  hideAddImageModal: () => void;
  showAddEventModal: () => void;
  hideAddEventModal: () => void;
  showImageSelectModal: (cb: (id: number) => void) => void;
  hideImageSelectModal: () => void;
  showAddTerrainTypeModal: () => void;
  hideAddTerrainTypeModal: () => void;
  showCreateRegionModal: (cb: (data: RegionFormData) => void) => void;
  hideCreateRegionModal: () => void;
  showGridSettingsModal: () => void;
  hideGridSettingsModal: () => void;
  showAddRelationshipModal: () => void;
  hideAddRelationshipModal: () => void;
  showAddManualCombatantModal: () => void;
  hideAddManualCombatantModal: () => void;
}

export const useUiStore = create<UiState & UiActions>((set) => ({
  // Initial State
  isEditModalOpen: false,
  editModalContext: null,
  isCharacterSheetModalOpen: false,
  characterSheetPlayer: null,
  isDiceRollerOpen: false,
  isSoundscapeOpen: false,
  isWatchOrderOpen: false,
  isSessionOpen: false,
  isScratchpadOpen: false,
  isCampaignLogOpen: false,
  isEncounterOpen: false,
  encounterResult: null,
  isAddImageOpen: false,
  isAddEventOpen: false,
  isImageSelectOpen: false,
  imageSelectCb: () => {},
  isAddTerrainOpen: false,
  isCreateRegionOpen: false,
  createRegionCb: () => {},
  isGridSettingsOpen: false,
  isAddRelationshipOpen: false,
  isAddManualCombatantOpen: false,

  // Actions
  showEditModal: (context) => {
    logger.info({ contextType: context.type }, 'showEditModal called via uiStore');
    set({ isEditModalOpen: true, editModalContext: context });
  },
  hideEditModal: () => set({ isEditModalOpen: false, editModalContext: null }),

  showCharacterSheetModal: (player) => {
    logger.info({ player: player.name }, 'showCharacterSheetModal called via uiStore');
    set({ isCharacterSheetModalOpen: true, characterSheetPlayer: player });
  },
  hideCharacterSheetModal: () => set({ isCharacterSheetModalOpen: false, characterSheetPlayer: null }),

  showDiceRollerModal: () => set({ isDiceRollerOpen: true }),
  hideDiceRollerModal: () => set({ isDiceRollerOpen: false }),
  showSoundscapeModal: () => set({ isSoundscapeOpen: true }),
  hideSoundscapeModal: () => set({ isSoundscapeOpen: false }),
  showWatchOrderModal: () => set({ isWatchOrderOpen: true }),
  hideWatchOrderModal: () => set({ isWatchOrderOpen: false }),
  showSessionModal: () => set({ isSessionOpen: true }),
  hideSessionModal: () => set({ isSessionOpen: false }),
  showScratchpadModal: () => set({ isScratchpadOpen: true }),
  hideScratchpadModal: () => set({ isScratchpadOpen: false }),
  showCampaignLogModal: () => set({ isCampaignLogOpen: true }),
  hideCampaignLogModal: () => set({ isCampaignLogOpen: false }),
  showEncounterModal: (result) => set({ isEncounterOpen: true, encounterResult: result }),
  hideEncounterModal: () => set({ isEncounterOpen: false }),
  showAddImageModal: () => set({ isAddImageOpen: true }),
  hideAddImageModal: () => set({ isAddImageOpen: false }),
  showAddEventModal: () => set({ isAddEventOpen: true }),
  hideAddEventModal: () => set({ isAddEventOpen: false }),
  showImageSelectModal: (cb) => set({ isImageSelectOpen: true, imageSelectCb: cb }),
  hideImageSelectModal: () => set({ isImageSelectOpen: false }),
  showAddTerrainTypeModal: () => set({ isAddTerrainOpen: true }),
  hideAddTerrainTypeModal: () => set({ isAddTerrainOpen: false }),
  showCreateRegionModal: (cb) => set({ isCreateRegionOpen: true, createRegionCb: cb }),
  hideCreateRegionModal: () => set({ isCreateRegionOpen: false }),
  showGridSettingsModal: () => set({ isGridSettingsOpen: true }),
  hideGridSettingsModal: () => set({ isGridSettingsOpen: false }),
  showAddRelationshipModal: () => set({ isAddRelationshipOpen: true }),
  hideAddRelationshipModal: () => set({ isAddRelationshipOpen: false }),
  showAddManualCombatantModal: () => set({ isAddManualCombatantOpen: true }),
  hideAddManualCombatantModal: () => set({ isAddManualCombatantOpen: false }),
}));